import { TrackingEventEnum } from '../../../main/constants/tracking-event.ts';
import { AppStateEnum } from '../../../main/types/app-state.types.ts';
import { getAudioPlayerUtil } from '../../../main/utils/audio/audio-player.util.ts';
import { getSpeechRecognitionUtil } from '../../../main/utils/audio/speech-recognition.util.ts';
import { getLocaleUtil } from '../../../main/utils/locale.util.ts';
import { getReduxStoreUtil } from '../../../main/utils/redux-store.util.ts';

import { PrototypeAnswer } from '../../constants/prototype-answer.ts';
import { SPEECH_RECOGNITION_MESSAGES } from '../../constants/speech-recognition.ts';
import { mapTranscriptToRecognizedText } from '../../mappers/transcript-to-recognized-text.ts';
import { selectCurrentLessonCharacter } from '../../selectors/current-lesson-character.selector.ts';
import { updateAppStateThunk } from '../../slices/app-state/thunks/update-app-state.thunk.ts';
import { setBallIsVisible } from '../../slices/ball/ball.slice.ts';
import { trackProgressionEventThunk } from '../../slices/progress/thunks/track-progression-event.thunk.ts';

import { getOpenResponseGraderUtil } from '../graders/open-response-grader.util.ts';

interface AskInitialHelpQuestionResult {
  didUserClaimsSuccess: boolean;
  firstResponse: string;
}

export class HelpUtil {
  private static _instance: HelpUtil;

  private constructor(
    private readonly audioPlayerUtil = getAudioPlayerUtil(),
    private readonly localeUtil = getLocaleUtil(),
    private readonly openResponseGraderUtil = getOpenResponseGraderUtil(),
    private readonly speechRecognitionUtil = getSpeechRecognitionUtil(),
    private readonly reduxStoreUtil = getReduxStoreUtil()
  ) {}

  public static get instance(): HelpUtil {
    if (!HelpUtil._instance) {
      HelpUtil._instance = new HelpUtil();
    }
    return HelpUtil._instance;
  }

  async offerHelp(): Promise<void> {
    const helpQuestion = this.localeUtil.select('learn:help_manager_help_question');
    const { didUserClaimsSuccess, firstResponse } = await this.askInitialHelpQuestion(helpQuestion);

    if (didUserClaimsSuccess) {
      await this.handleUserFeedback(helpQuestion, firstResponse);
      await this.completeStepAsCorrect();
    } else {
      await this.handleRetry(helpQuestion, firstResponse);
    }
  }

  private async askInitialHelpQuestion(helpQuestion: string): Promise<AskInitialHelpQuestionResult> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();

    const character = selectCurrentLessonCharacter(state);

    this.audioPlayerUtil.speak(helpQuestion, character);
    await this.audioPlayerUtil.waitSpeakCompletion();

    dispatch(setBallIsVisible(true));
    const result = await this.speechRecognitionUtil.recognizeSpeech({
      silenceDetection: {
        enabled: true,
      },
    });
    dispatch(setBallIsVisible(false));

    let recognizedText: string = SPEECH_RECOGNITION_MESSAGES.NO_RESPONSE;
    if (!result.success || !result.isFinal) {
      recognizedText = SPEECH_RECOGNITION_MESSAGES.ERROR;
    } else {
      recognizedText = mapTranscriptToRecognizedText(result.transcript);
    }

    const { isCorrect } = await this.openResponseGraderUtil.checkAnswer({
      story: '',
      question: helpQuestion,
      receivedAnswer: recognizedText,
      prototypeAnswer: PrototypeAnswer.YES,
    });

    return { didUserClaimsSuccess: isCorrect, firstResponse: recognizedText };
  }

  private async handleUserFeedback(helpQuestion: string, firstResponse: string): Promise<void> {
    const dispatch = await this.reduxStoreUtil.getDispatch();
    const state = await this.reduxStoreUtil.getRootState();

    const character = selectCurrentLessonCharacter(state);

    const feedbackQuestion = this.localeUtil.select('learn:help_manager_feedback_question');
    this.audioPlayerUtil.speak(feedbackQuestion, character);
    await this.audioPlayerUtil.waitSpeakCompletion();

    const secondResponse = await this.recordUserFeedback();

    dispatch(
      trackProgressionEventThunk({
        event: TrackingEventEnum.STEP_CHOSE_TO_SKIP,
        feedback: { [helpQuestion]: firstResponse, [feedbackQuestion]: secondResponse },
      })
    );
  }

  private async recordUserFeedback(): Promise<string> {
    const dispatch = await this.reduxStoreUtil.getDispatch();

    dispatch(setBallIsVisible(true));
    const result = await this.speechRecognitionUtil.recognizeSpeech({
      silenceDetection: {
        enabled: true,
      },
    });
    dispatch(setBallIsVisible(false));

    if (!result.success || !result.isFinal) {
      return SPEECH_RECOGNITION_MESSAGES.ERROR;
    }

    return mapTranscriptToRecognizedText(result.transcript);
  }

  private async handleRetry(helpQuestion: string, firstResponse: string): Promise<void> {
    const state = await this.reduxStoreUtil.getRootState();
    const dispatch = await this.reduxStoreUtil.getDispatch();

    const character = selectCurrentLessonCharacter(state);
    this.audioPlayerUtil.speak(this.localeUtil.select('learn:help_manager_retry'), character);

    dispatch(
      trackProgressionEventThunk({
        event: TrackingEventEnum.STEP_CHOSE_TO_RETRY,
        feedback: { [helpQuestion]: firstResponse },
      })
    );

    await this.audioPlayerUtil.waitSpeakCompletion();
    await dispatch(updateAppStateThunk({ state: AppStateEnum.ReadyToStart, blockAnimation: true })).unwrap();
  }

  private async completeStepAsCorrect(): Promise<void> {
    const state = await this.reduxStoreUtil.getRootState();

    const dispatch = await this.reduxStoreUtil.getDispatch();

    const character = selectCurrentLessonCharacter(state);
    this.audioPlayerUtil.speak(this.localeUtil.select('learn:help_manager_complete_step_as_correct'), character);

    await this.audioPlayerUtil.waitSpeakCompletion();

    await dispatch(updateAppStateThunk({ state: AppStateEnum.Correct, blockAnimation: true })).unwrap();
  }
}

export const getHelpUtil = (): HelpUtil => HelpUtil.instance;
